/**
 * The hierarchy template designer's contract — campusone-identity-service's
 * institution-templates module, mirrored field for field from
 * InstitutionTemplateDefinition in that service's contracts/template-shape.ts.
 *
 * NOTE THE SUBSYSTEM ENUM. Templates use NOT_APPLICABLE where the positions API
 * uses GENERAL — the two enums are different types in identity and they are kept
 * different here, because sending GENERAL to the resolve endpoint is a 400.
 */

export const TEMPLATE_SUBSYSTEMS = [
  'ANGLOPHONE',
  'FRANCOPHONE',
  'BILINGUAL',
  'NOT_APPLICABLE',
] as const;

export type TemplateSubsystem = (typeof TEMPLATE_SUBSYSTEMS)[number];

export const CAPABILITY_CLASSES = ['OPERATIONAL', 'SUPERVISORY', 'AGGREGATE'] as const;

export type CapabilityClass = (typeof CAPABILITY_CLASSES)[number];

/**
 * The unit types a hierarchy rung can be — identity's HierarchyUnitType, all 28.
 * The designer offers these in the experiment picker; the server re-validates.
 */
export const HIERARCHY_UNIT_TYPES = [
  'INSTITUTION',
  'CAMPUS',
  'SECTION',
  'CYCLE',
  'LEVEL',
  'STREAM',
  'CLASS_GROUP',
  'FACULTY',
  'SCHOOL_DIVISION',
  'DEPARTMENT',
  'PROGRAMME',
  'COURSE_OFFERING',
  'GRADUATE_SCHOOL',
  'RESEARCH_CENTRE',
  'ADMINISTRATIVE_DIRECTORATE',
  'CENTRE',
  'TRAINING_DIVISION',
  'INTAKE_COHORT',
  'MODULE',
  'TRAINING_CENTRE',
  'TRADE_DIVISION',
  'TRADE_PROGRAMME',
  'COMPETENCY_LEVEL',
  'WORKSHOP_GROUP',
  'GROUP',
  'CENTRAL_OFFICE',
  'REGIONAL_OFFICE',
  'DIVISIONAL_OFFICE',
  'MEMBER_INSTITUTION',
  'SUPERVISED_INSTITUTION',
] as const;

export type HierarchyUnitType = string;

/**
 * The unit types where teaching actually happens — identity's DELIVERY_UNIT_TYPES,
 * same members, same order. A SUPERVISORY or AGGREGATE body can never hold one:
 * the server throws PRIVILEGE_ESCALATION_DENIED, and the designer disables them
 * up front so the rule is learned from a greyed-out option instead of a 403.
 */
export const DELIVERY_UNIT_TYPES = [
  'CLASS_GROUP',
  'COURSE_OFFERING',
  'MODULE',
  'WORKSHOP_GROUP',
  'INTAKE_COHORT',
  'STREAM',
] as const;

export function isDeliveryUnitType(unitType: string): boolean {
  return (DELIVERY_UNIT_TYPES as readonly string[]).includes(unitType);
}

export interface HierarchyRung {
  unitType: HierarchyUnitType;
  /** 1-based. The server recomputes it from array order on preview; display only here. */
  depth: number;
  label: string;
  required: boolean;
}

export interface TemplateCycle {
  code: string;
  label: string;
  /**
   * DISPLAY TEXT ONLY — "Class 1 – Class 6". Never parse it into a level picker:
   * the real levels live in EduCore's level templates (see levelSource).
   */
  levelRangeLabel: string;
  ordinal: number;
}

export interface TemplateSection {
  code: string;
  label: string;
  subsystem: TemplateSubsystem;
  ordinal: number;
  cycles: TemplateCycle[];
}

export interface TemplateLevelSource {
  source: string;
  institutionModel: string;
  subsystems: string[];
}

export interface TemplateAcademicModel {
  kind: string;
  calendar: { periodType: string; periodsPerYear: number; typicalStartMonth: number };
  grading: { scheme: string; passMark: number | null };
  progression: { mode: string; repetitionAllowed: boolean };
  certifications: string[];
}

export interface TemplatePositionDefault {
  positionCode: string;
  positionTemplateId?: string;
  /** The rung the office anchors to — must exist in hierarchy ∪ auxiliaryUnitTypes. */
  scopeUnitType: HierarchyUnitType;
  /** Narrows the anchor to one section, e.g. 'ANG-PRI'. Null means every unit of the type. */
  scopeSelector: string | null;
  capabilities: string[];
}

/** The 12 terminology keys a template carries — identity's TerminologyKey. */
export const TEMPLATE_TERMINOLOGY_KEYS = [
  'learner',
  'learnerPlural',
  'educator',
  'educatorPlural',
  'teachingGroup',
  'headOfInstitution',
  'academicUnit',
  'programmeOfStudy',
  'academicPeriod',
  'academicYear',
  'assessment',
  'guardian',
] as const;

export type TemplateTerminologyKey = (typeof TEMPLATE_TERMINOLOGY_KEYS)[number];

export interface InstitutionTemplateDefinition {
  code: string;
  institutionType: string;
  educationSubsystem: TemplateSubsystem;
  displayName: string;
  description: string;
  capabilityClass: CapabilityClass;
  hierarchy: HierarchyRung[];
  auxiliaryUnitTypes: HierarchyUnitType[];
  sections: TemplateSection[];
  levelSource: TemplateLevelSource | null;
  terminology: Partial<Record<TemplateTerminologyKey, string>>;
  academicModel: TemplateAcademicModel;
  defaultPositions: TemplatePositionDefault[];
  defaultRoleBundleCodes: string[];
  needsReview: boolean;
}

/**
 * POST /institution-templates/resolve/customised — the preview body. Everything
 * beyond the two resolve keys is optional; hierarchy and auxiliaryUnitTypes are
 * replaced wholesale, terminology is merged shallowly, and NOTHING persists.
 */
export interface TemplateCustomisationRequest {
  institutionType: string;
  educationSubsystem?: TemplateSubsystem;
  hierarchy?: HierarchyRung[];
  auxiliaryUnitTypes?: HierarchyUnitType[];
  additionalPositions?: TemplatePositionDefault[];
  terminology?: Record<string, string>;
  capabilityClass?: CapabilityClass;
}
